import React, { useContext, useEffect, useState, useMemo, FC } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import CircularProgress from '@mui/material/CircularProgress'

import { BarChart, Bar, XAxis, YAxis, Legend, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts'
import moment from 'moment'
import { camelCase } from 'lodash'

import { isValidRUC } from 'containers/STRTablePage/utils'
import { VerticalContext } from 'store/verticals/Context'
import Tabs from 'components/common/Tabs'
import ContactInfo from 'containers/VerticalPage/common/ContactInfo/ContactInfo'
import GraphContainer from 'containers/VerticalPage/common/GraphContainer/GraphContainer'
import Map from 'components/Map/Map'
import BusinessSource from 'components/BusinessSource'
import MuiTooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import { getAbbreviatedNumberString } from 'components/Income/Income'
import { extractRootDomainNoExt } from 'containers/NFT/specs'
import { allowedSources, originalSourceToImageMap, SOURCE_GLOBE } from 'components/BusinessSource/specs'
import { selectCurrentZone, selectMapBaseUrl, selectMapZoomDelta, selectPlatform } from 'store/global'
import { ContactInfoSections, IrrelevantContactSections } from 'containers/VerticalPage/common/types'
import type { CurrentZone } from 'utils/types'
import type { ListingsLocations } from 'store/verticals/types'
import { fetchUiConfig } from 'services/strAdminApi'
import { REVENUE_TOOLTIP_TEXT } from '../revenueUtils'

import styles from './Content.scss'

const placeholderIcon: string = require('components/EntityCard/images/placeholder.svg')
const calendarIcon: string = require('components/EntityCard/images/cake.svg')
const markerIcon: string = require('components/EntityCard/images/marker.svg')
const xIcon: string = require('components/BusinessSource/assets/original/x.svg')

const tabsIds = { OVERVIEW: 0, CONTACT_INFO: 1 } as const
const tabsList = [
  { id: tabsIds.OVERVIEW, label: 'Overview' },
  { id: tabsIds.CONTACT_INFO, label: 'Contact info' },
]

const overviewMapping = [
  { key: 'activityType', label: 'Activity Type' },
  { key: 'firstActivity', label: 'First Activity' },
  { key: 'documentedRevenue', label: 'Total Revenue' },
  { key: 'reportedRevenue', label: 'Reported Revenue' },
  { key: 'taxGap', label: 'Unreported Revenue' },
]

const irrelevantContactSections: IrrelevantContactSections = [ContactInfoSections.wallets]

const listingsTableHeaders = ['Listing ID', 'URL', 'Address', 'Reviews', 'Documented Revenue']

const CHART_COLORS = {
  reported: '#1C2842',
  estimated: '#2196F3',
  expenses: '#FF9800',
}

const TOOLTIP_SX = {
  '&.MuiTooltip-tooltip': {
    maxWidth: 340,
    fontSize: '16px !important',
    lineHeight: '24px !important',
    color: 'rgba(0,0,0,0.85)',
    backgroundColor: '#fff',
    border: '1px solid #d9d9d9',
    borderRadius: '4px',
    padding: '14px 18px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
} as const

const TOOLTIP_ARROW_SX = {
  color: '#fff',
  '&::before': { border: '1px solid #d9d9d9' },
} as const

const TOOLTIP_CONTENT = <span><b>Estimated</b> range based on two independent methods: review-based analysis and market comparison.</span>

const RevenueInfoTooltip: FC = () => (
  <MuiTooltip title={TOOLTIP_CONTENT} arrow placement='top' slotProps={{ tooltip: { sx: TOOLTIP_SX }, arrow: { sx: TOOLTIP_ARROW_SX } }}>
    <InfoOutlinedIcon sx={{ fontSize: '16px', color: 'rgba(0,0,0,0.35)', cursor: 'help', verticalAlign: 'middle', ml: '4px' }} />
  </MuiTooltip>
)

// Figma: table header — Roboto Medium 500, 14px/21px, rgba(0,0,0,0.45), uppercase
const TABLE_HEADER_SX = {
  fontFamily: 'Roboto, sans-serif',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '21px',
  letterSpacing: 0,
  color: 'rgba(0, 0, 0, 0.45)',
  textTransform: 'uppercase',
  border: 'none',
} as const

// Figma: table cell — Roboto Regular 400, 16px/24px, rgba(0,0,0,0.85)
const TABLE_CELL_SX = {
  fontFamily: 'Roboto, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: 0,
  color: 'rgba(0, 0, 0, 0.85)',
} as const

const Content: FC = () => {
  const { entityId } = useParams<{ entityId: string }>()
  const { getVerticalEntity, getVerticalBarChartsData, verticalEntity, verticalReportedRevenue } =
    useContext(VerticalContext)
  const currentZone: CurrentZone = useSelector(selectCurrentZone)
  const platform = useSelector(selectPlatform)
  const mapBaseUrl = useSelector(selectMapBaseUrl)
  const mapZoomDelta = useSelector(selectMapZoomDelta)

  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<number>(tabsIds.OVERVIEW)
  const [imageError, setImageError] = useState(false)
  const [selectedListingRow, setSelectedListingRow] = useState<ListingsLocations & { listingUrl: string }>()

  const { data: uiConfigData } = useQuery({
    queryKey: ['str-ui-config'],
    queryFn: fetchUiConfig,
    staleTime: 5 * 60 * 1000,
  })
  const ep = uiConfigData?.entityPage

  useEffect(() => {
    if (!entityId || !currentZone) return

    if (currentZone.strIndices) {
      const fetchData = async () => {
        setIsLoading(true)
        await getVerticalEntity(entityId, currentZone.strIndices)
        setIsLoading(false)
      }
      fetchData()
    }
  }, [currentZone, entityId])

  useEffect(() => {
    if (!entityId || !currentZone?.strActivityIndices) return
    getVerticalBarChartsData(entityId, { start: '', end: '' }, currentZone.strActivityIndices)
  }, [currentZone, entityId])

  useEffect(() => {
    setImageError(false)
  }, [verticalEntity?.entityDetails?.image])

  const handleTabClick = (_event: React.SyntheticEvent, value: number) => {
    setActiveTab(value)
  }

  const entityDetails = verticalEntity?.entityDetails
  const overviewDetails = verticalEntity?.overviewDetails
  const totals = verticalEntity?.totals as Record<string, any> | undefined
  const contactInfo = verticalEntity?.contactInfo
  const revenueEnabled = !!(verticalEntity as any)?.revenueEnabled

  const revenueLow = totals?.estimatedRevenueLow
  const revenueHigh = totals?.estimatedRevenueHigh
  const hasRevenueRange = revenueLow != null && revenueHigh != null

  const getSourceSrc = (url: string) => {
    const domain = extractRootDomainNoExt(url)
    if (domain === 'x' || domain === 'twitter') return xIcon
    return allowedSources.includes(domain) ? originalSourceToImageMap[domain] : originalSourceToImageMap[SOURCE_GLOBE]
  }

  const socialUrls = useMemo(() => {
    if (!contactInfo?.weblinks) return []
    return Object.values(contactInfo.weblinks).flat()
  }, [contactInfo?.weblinks])

  const mapLocations = useMemo(() => {
    if (!verticalEntity?.revenueSources?.length) return []
    return verticalEntity.revenueSources
      .filter(s => s.listingsLocations?.coordinates)
      .map(s => ({
        ...s.listingsLocations!,
        listingId: s.profileName || s.listingId || '',
        listingAddressEnriched: s.listingsLocations?.resolvedAddress || s.listingsLocations?.listingAddressEnriched || '',
        listingUrl: s.profileUrl || '',
      }))
  }, [verticalEntity?.revenueSources])

  const formatCurrency = (value: number | null | undefined): string => {
    if (value == null) return 'N/A'
    return `$${getAbbreviatedNumberString(value)}`
  }

  if (isLoading && !verticalEntity) {
    return (
      <Stack justifyContent='center' alignItems='center' sx={{ p: 6, gridColumn: '1 / 2' }}>
        <CircularProgress />
      </Stack>
    )
  }

  if (!verticalEntity) return null

  return (
    <div className={styles.strEntityPage}>
      {/* Entity Header Card */}
      <div className={styles.entityHeader}>
        <div className={styles.entityHeaderLeft}>
          {entityDetails?.image && !imageError ? (
            <img className={styles.avatar} src={entityDetails.image} alt='Avatar' onError={() => setImageError(true)} />
          ) : (
            <img className={styles.avatar} src={placeholderIcon} alt='Avatar' />
          )}
          <div className={styles.entityInfo}>
            <h2 className={styles.entityName}>
              {entityDetails?.name || 'N/A'}
              {isValidRUC(entityDetails?.tin) && (
                <span className={styles.tinLabel}> | TIN: <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{entityDetails.tin}</span></span>
              )}
            </h2>
            <div className={styles.metadataRow}>
              {entityDetails?.birthday && (
                <span className={styles.metadataItem}>
                  <img className={styles.metadataIcon} src={calendarIcon} alt='' />
                  <span className={styles.metadataLabel}>Join date</span>{' '}
                  <span className={styles.metadataValue}>{moment(entityDetails.birthday).format('MMMM Do, YYYY')}</span>
                </span>
              )}
              {entityDetails?.location && (
                <span className={styles.metadataItem}>
                  <img className={styles.metadataIcon} src={markerIcon} alt='' />
                  <span className={styles.metadataLabel}>Location</span>{' '}
                  <span className={styles.metadataValue}>{entityDetails.location}</span>
                </span>
              )}
              {totals?.numberOfReviews != null && (
                <MuiTooltip
                  title={<span>Total reviews across this host's displayed listings only, not all reviews for the host.</span>}
                  arrow
                  placement='top'
                  slotProps={{ tooltip: { sx: TOOLTIP_SX }, arrow: { sx: TOOLTIP_ARROW_SX } }}
                >
                  <span className={styles.metadataItem} style={{ cursor: 'help' }}>
                    <span className={styles.metadataLabel}>Number of Reviews</span>{' '}
                    <span className={styles.metadataValue}>
                      {totals.numberOfReviews.toLocaleString()}
                    </span>
                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: 'rgba(0,0,0,0.3)' }} />
                  </span>
                </MuiTooltip>
              )}
              {socialUrls.length > 0 && (
                <span className={styles.metadataItem}>
                  <span className={styles.metadataLabel}>Social:</span>
                  <span className={styles.socialIcons}>
                    {socialUrls.map(url => (
                      <a key={url} href={url} target='_blank' rel='noreferrer'>
                        <img className={styles.socialIcon} src={getSourceSrc(url)} alt='Social' />
                      </a>
                    ))}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
        {currentZone?.strGraphIndices && (
          <GraphContainer zone={currentZone.strGraphIndices} overviewMapping={overviewMapping} />
        )}
      </div>

      {/* Tabs */}
      <div className={styles.tabsWrapper}>
        <Tabs
          tabs={tabsList}
          value={activeTab}
          tabsClasses={{ root: styles.root, flexContainer: styles.flexContainer }}
          tabClasses={{ root: styles.tabRoot }}
          onChange={handleTabClick}
        />
        {revenueEnabled && (
          <MuiTooltip title={<span>Date range cannot be changed — this reflects all available data.</span>} arrow placement='bottom' slotProps={{ tooltip: { sx: TOOLTIP_SX }, arrow: { sx: TOOLTIP_ARROW_SX } }}>
            <div className={styles.datePicker} style={{ cursor: 'help' }}>
              <span className={styles.datePickerValue}>2025-01-01</span>
              <span style={{ fontSize: '16px', color: 'rgba(0,0,0,0.25)', margin: '0 4px', display: 'inline-flex', alignItems: 'center' }}>~</span>
              <span className={styles.datePickerValue}>2025-12-31</span>
              <CalendarTodayOutlinedIcon sx={{ fontSize: '14px', color: 'rgba(0,0,0,0.25)', ml: '8px' }} />
            </div>
          </MuiTooltip>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === tabsIds.OVERVIEW && (
        <>
          {/* Revenue Summary (only when revenue is disabled and config allows) */}
          {!revenueEnabled && ep?.revenueSummary !== false && (
            <div className={styles.revenueSummary}>
              Total Revenue :{' '}
              <span className={styles.revenueSummaryValue}>{formatCurrency(overviewDetails?.documentedRevenue)}</span> |
              Unreported Revenue{' '}
              <span className={styles.revenueSummaryValue}>{formatCurrency(overviewDetails?.taxGap)}</span>
            </div>
          )}

          {/* Bar Chart */}
          {ep?.chart !== false && (
            <div className={styles.chartContainer}>
              {verticalReportedRevenue && verticalReportedRevenue.length > 0 ? (
                <ResponsiveContainer width='100%' height={250}>
                  <BarChart data={verticalReportedRevenue} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} />
                    <XAxis dataKey='year' tickLine={false} />
                    <YAxis tickLine={false} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey='reported' name='Reported' fill={CHART_COLORS.reported} radius={[4, 4, 0, 0]} />
                    <Bar dataKey='estimated' name='Estimated' fill={CHART_COLORS.estimated} radius={[4, 4, 0, 0]} />
                    <Bar dataKey='expenses' name='Expenses' fill={CHART_COLORS.expenses} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className={styles.chartPlaceholder}>No chart data</div>
              )}
            </div>
          )}

          {/* Listing Section Title */}
          {!revenueEnabled && ep?.sectionTitle !== false && (
            <h3 className={styles.sectionTitle}>Listing</h3>
          )}

          {/* Financial Stats Card — revenue-enabled hosts */}
          {revenueEnabled && (
            <div className={styles.statsCard}>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>
                  Estimated Annual Revenue
                  <RevenueInfoTooltip />
                </span>
                <span className={styles.statValue}>
                  {hasRevenueRange
                    ? `$${revenueLow!.toLocaleString()} – $${revenueHigh!.toLocaleString()}`
                    : 'N/A'}
                </span>
              </div>
            </div>
          )}
          {/* Financial Stats Card — legacy (revenue disabled) */}
          {!revenueEnabled && ep?.statsCard !== false && (
            <div className={styles.statsCard}>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Documented Revenue</span>
                <span className={styles.statValue}>{formatCurrency(overviewDetails?.documentedRevenue)}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Reported Revenue</span>
                <span className={styles.statValue}>{formatCurrency(overviewDetails?.reportedRevenue)}</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Reported Last Year</span>
                <span className={styles.statValue}>N/A</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Tax Rate</span>
                <span className={styles.statValue}>N/A</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Tax Gap</span>
                <span className={styles.statValue}>{formatCurrency(overviewDetails?.taxGap)}</span>
              </div>
            </div>
          )}

          {/* Map */}
          {mapLocations.length > 0 ? (
            <div className={styles.mapContainer}>
              <Map
                platform={platform}
                widgets={['zoomLevel']}
                baseUrl={mapBaseUrl}
                zoomDelta={mapZoomDelta}
                locations={[
                  {
                    list: selectedListingRow ? [selectedListingRow] : mapLocations,
                    listColor: '#1C2842',
                  },
                ]}
                pointsMode
              />
            </div>
          ) : (
            <div className={styles.mapPlaceholder}>No location data</div>
          )}

          {/* Listings Table */}
          <div className={styles.listingsTable}>
            {verticalEntity.revenueSources?.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {listingsTableHeaders
                        .filter(head => head !== 'Documented Revenue' || ep?.listingDocumentedRevenue !== false)
                        .map(head => (
                          <TableCell key={head} sx={TABLE_HEADER_SX}>
                            {head}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {verticalEntity.revenueSources.map((row, index) => {
                      const domain = extractRootDomainNoExt(row.profileUrl)
                      const docRevenue = row.revenueByYear?.documented
                        ? Object.values(row.revenueByYear.documented).reduce((sum, val) => sum + (Number(val) || 0), 0)
                        : null
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                            cursor: 'pointer',
                            ...(selectedListingRow?.listingId === row.profileName && Boolean(row.profileName)
                              ? { backgroundColor: '#007bff0a' }
                              : {}),
                          }}
                          onClick={() => {
                            if (selectedListingRow?.listingId === row.profileName && Boolean(row.profileName)) {
                              setSelectedListingRow(undefined)
                            } else {
                              setSelectedListingRow({
                                listingId: row?.profileName || 0,
                                listingAddressEnriched: row?.listingsLocations?.resolvedAddress || row?.listingsLocations?.listingAddressEnriched || '',
                                coordinates: row?.listingsLocations?.coordinates || '',
                                listingUrl: row.profileUrl || '',
                                listingStateLocator: row?.listingsLocations?.listingStateLocator || '',
                              })
                            }
                          }}
                        >
                          <TableCell sx={TABLE_CELL_SX}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {row?.profileName || 'N/A'}
                              {selectedListingRow?.listingId === row.profileName && Boolean(row.profileName) && (
                                <span
                                  title='Deselect listing'
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: '18px', height: '18px', borderRadius: '50%',
                                    backgroundColor: 'rgba(0,0,0,0.08)', color: '#666',
                                    fontSize: '13px', lineHeight: 1, cursor: 'pointer', flexShrink: 0,
                                  }}
                                  onClick={(e) => { e.stopPropagation(); setSelectedListingRow(undefined) }}
                                >
                                  &#x2715;
                                </span>
                              )}
                            </span>
                          </TableCell>
                          <TableCell>
                            <a href={row.profileUrl} target='_blank' rel='noreferrer'>
                              <BusinessSource source={camelCase(domain)} isClickable />
                            </a>
                          </TableCell>
                          <TableCell sx={TABLE_CELL_SX}>
                            {row?.listingsLocations?.resolvedAddress
                              || [
                                row?.listingsLocations?.listingAddressEnriched,
                                row?.listingsLocations?.listingStateLocator,
                              ]
                                .filter(Boolean)
                                .join(', ') || 'N/A'}
                          </TableCell>
                          <TableCell sx={TABLE_CELL_SX}>
                            {row?.numberOfReviews != null ? row.numberOfReviews.toLocaleString() : 'N/A'}
                          </TableCell>
                          {ep?.listingDocumentedRevenue !== false && (
                            <TableCell sx={TABLE_CELL_SX}>
                              {docRevenue != null ? `$${docRevenue.toLocaleString()}` : 'N/A'}
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Stack justifyContent='center' alignItems='center' sx={{ p: 3 }}>
                No listings data
              </Stack>
            )}
          </div>
        </>
      )}

      {activeTab === tabsIds.CONTACT_INFO && <ContactInfo irrelevantSections={irrelevantContactSections} />}
    </div>
  )
}

export default Content
